import { IonIcon } from "@ionic/react";
import {
  appleIconSVG,
  calendarOverlapSVG,
  calendarSVG,
  checkSquareSVG,
  crownSVG,
  googleSVG,
  lightBulbSVG,
  permitIconSVG,
  schoolIconSVG,
  squareSVG,
  starCircleSVG,
  swingHeroSVG,
  teacherIconSVG,
  ukgSVG,
} from "swing-assets";

import { ValuesOf } from "~utils";

import { Colors } from "../Colors";

/********** Component **********/
export type IconName = keyof typeof ICONS;

type IconProps<Name extends IconName> = {
  name: Name;
  /**
   * [Optional] Change the color of the icon.
   * @default `Colors.black`
   */
  color?: ValuesOf<typeof Colors> | "currentColor";
};

/**
 * Renders an icon part of the Swing Education design system
 */
export function Icon<Name extends keyof typeof ICONS>(props: IconProps<Name>) {
  const { name, color = Colors.black } = props;

  if ("svg" in ICONS[name]) {
    return (
      // TODO: @KoltonG - Remove the reliance of this and check how they are
      // injecting the SVG directly as we can do the same.
      <IonIcon
        src={(ICONS[name] as { svg: string }).svg}
        style={{ color, height: 24, width: 24 }}
      />
    );
  }

  // Some icons are outlined. This will be defined in the ICONS object.
  const isOutline = (ICONS[name] as { isOutline?: boolean }).isOutline;

  return (
    <span
      className="material-symbols-rounded"
      style={{
        // remove unwanted whitespace from material-symbols-rounded using `inline-block`
        display: "block",
        color,
        // https://developers.google.com/fonts/docs/material_symbols#variable_font_with_google_fonts
        fontVariationSettings: `"FILL" ${isOutline ? 0 : 1}`,
      }}
    >
      {(ICONS[name] as { fontName: string }).fontName}
    </span>
  );
}

/********** Constants **********/
/**
 * Defined icons in the following format:
 *
 * - `Add`: { fontName: "add" } - for icons that are not outlined
 * - `Apple`: { svg: appleIconSVG } - for icons that are SVGs
 *   - Note that the SVG files will need to be updated so that all `fill` colors are `currentColor`
 * - `Favorite Unfilled`: { fontName: "favorite", isOutline: true } - for icons that are outlined
 *
 * @see
 * - [Material Icons](https://fonts.google.com/icons)
 */
export const ICONS = {
  Accessible: { fontName: "accessible" },
  Add: { fontName: "add" },
  Alarm: { fontName: "alarm" },
  Alert: { fontName: "error" },
  Apple: { svg: appleIconSVG },
  "Arrow Down": { fontName: "arrow_downward" },
  "Arrow Left": { fontName: "arrow_back" },
  "Arrow Right": { fontName: "arrow_forward" },
  "Arrow Up": { fontName: "arrow_upward" },
  Assignment: { fontName: "assignment" },
  Award: { fontName: "social_leaderboard", isOutline: true },
  Block: { fontName: "block" },
  Book: { fontName: "menu_book" },
  "Bookmark Check": { fontName: "bookmark_check" },
  BookmarkCheck: { fontName: "bookmark_check", isOutline: true },
  Briefcase: { fontName: "work" },
  "Calendar Check": { fontName: "event_available" },
  Calendar: { svg: calendarSVG },
  "Calendar Overlap": { svg: calendarOverlapSVG },
  "Calendar Today": { fontName: "calendar_today" },
  Car: { fontName: "directions_car" },
  Celebration: { fontName: "celebration" },
  "Chat Bubble": { fontName: "chat_bubble" },
  "Chat Bubble Active": { fontName: "sms" },
  Check: { fontName: "check" },
  "Check Circle": { fontName: "check_circle" },
  "Check Square": { svg: checkSquareSVG },
  "Chevron Down": { fontName: "keyboard_arrow_down" },
  "Chevron Left": { fontName: "keyboard_arrow_left" },
  "Chevron Right": { fontName: "keyboard_arrow_right" },
  "Chevron Up": { fontName: "keyboard_arrow_up" },
  Clipboard: { fontName: "content_paste" },
  Clock: { fontName: "schedule" },
  Close: { fontName: "close" },
  Crown: { svg: crownSVG },
  Delete: { fontName: "delete_forever" },
  Download: { fontName: "download" },
  Edit: { fontName: "edit" },
  Enter: { fontName: "login" },
  "External Link": { fontName: "open_in_new" },
  "Eye Glasses": { fontName: "eyeglasses" },
  "Eye Off": { fontName: "visibility_off" },
  "Eye On": { fontName: "visibility" },
  "Face Negative": { fontName: "sentiment_dissatisfied", isOutline: true },
  "Face Neutral": { fontName: "sentiment_neutral", isOutline: true },
  "Face Positive": { fontName: "sentiment_satisfied", isOutline: true },
  "Favorite Filled": { fontName: "favorite" },
  "Favorite Unfilled": { fontName: "favorite", isOutline: true },
  Filter: { fontName: "filter_alt" },
  Fingerprint: { fontName: "fingerprint" },
  Flag: { fontName: "flag" },
  Food: { fontName: "fastfood" },
  Gift: { fontName: "featured_seasonal_and_gifts" },
  // NOTE: This icon is the only one that does not support colors
  Google: { svg: googleSVG },
  "Grad Cap": { fontName: "school" },
  Group: { fontName: "group" },
  Groups: { fontName: "groups" },
  Handshake: { fontName: "handshake" },
  Health: { fontName: "health_and_safety" },
  Help: { fontName: "help" },
  History: { fontName: "history" },
  Home: { fontName: "home" },
  Hourglass: { fontName: "hourglass_top" },
  Info: { fontName: "info" },
  Laptop: { fontName: "laptop_windows", isOutline: true },
  Lesson: { fontName: "book_2", isOutline: true },
  License: { fontName: "verified", isOutline: true },
  "Light Bulb": { svg: lightBulbSVG },
  List: { fontName: "list" },
  Location: { fontName: "location_on" },
  Lock: { fontName: "lock" },
  Logout: { fontName: "logout" },
  Mail: { fontName: "mail" },
  Map: { fontName: "map" },
  Mask: { fontName: "masks" },
  "Menu Burger": { fontName: "menu" },
  "Menu Meatball": { fontName: "more_horiz" },
  "Mobile Phone": { fontName: "phone_iphone" },
  Monetization: { fontName: "paid" },
  Note: { fontName: "sticky_note_2" },
  "Notification Add": { fontName: "notification_add" },
  Notifications: { fontName: "notifications" },
  Overview: { fontName: "overview", isOutline: true },
  Payments: { fontName: "payments" },
  "Pending Actions": { fontName: "pending_actions" },
  Permit: { svg: permitIconSVG },
  Person: { fontName: "person" },
  "Person Add": { fontName: "person_add" },
  "Person Off": { fontName: "person_off" },
  Phone: { fontName: "call" },
  Policy: { fontName: "policy" },
  Progress: { fontName: "progress_activity" },
  "Punch Clock": { fontName: "punch_clock" },
  Refresh: { fontName: "refresh" },
  "Rocket Launch": { fontName: "rocket_launch" },
  School: { svg: schoolIconSVG },
  Search: { fontName: "search" },
  Send: { fontName: "send" },
  Settings: { fontName: "settings" },
  Share: { fontName: "share" },
  Shield: { fontName: "verified_user" },
  Shirt: { fontName: "apparel" },
  Signature: { fontName: "signature" },
  Sort: { fontName: "swap_vert" },
  Square: { svg: squareSVG },
  Star: { fontName: "star" },
  "Star Circle": { svg: starCircleSVG },
  Sun: { fontName: "sunny" },
  "Swing Hero": { svg: swingHeroSVG },
  Teacher: { svg: teacherIconSVG },
  "Thumb Down": { fontName: "thumb_down", isOutline: true },
  "Thumb Up": { fontName: "thumb_up", isOutline: true },
  UKG: { svg: ukgSVG },
  Upload: { fontName: "upload" },
  Verified: { fontName: "verified" },
  Warning: { fontName: "warning" },
  Website: { fontName: "language" },
} as const;
